import React, { useMemo } from 'react';
import { isVisibleAtScale } from '@agissc/ktag-map';
import useAppState from '../../../../AppStore';
import { useLegendQuery } from '../../api/useLegendQuery';
import { useMetadataQuery } from '../../api/useMetadataQuery';
import GroupLayerSetting from './GroupLayerSetting';
import {
  MapServiceLayerMetadata,
  useTopicMetadataQuery,
} from '../../api/useTopicMetadataQuery';
import useSubLayersSetting from '../../api/useSubLayersSetting';
import EsriLayerSettingsPlaceholder from './EsriLayerSettingsPlaceholder';
import { useTracking } from '../../../../utils/tracking/TrackingContext';

export interface EsriLayerSettingsProps {
  topicId: string;
  url: string;
  isPublic?: boolean;
}

const EsriLayerSettings = ({ topicId, url, isPublic = true }: EsriLayerSettingsProps) => {
  const location = useAppState((state) => state.location);
  const setSubLayerSetting = useAppState((state) => state.setSubLayerSetting);

  const legendQuery = useLegendQuery(url, isPublic);
  const layersConfigQuery = useMetadataQuery(topicId);
  const mapLayerSetting = useSubLayersSetting(topicId, url, isPublic);
  const metadataQuery = useTopicMetadataQuery(topicId, isPublic);
  const { Tracking } = useTracking();

  const legend = legendQuery.data ?? {};
  const layersConfig = layersConfigQuery.data ?? {};
  const layerSetting = mapLayerSetting.data ?? {};

  const visibleSublayerIdsAtScale = useMemo(() => {
    return Object.values(layerSetting).reduce(
      (acc, layer) => {
        return {
          ...acc,
          [layer.id]: isVisibleAtScale(
            location.resolution,
            layer.minScale,
            layer.maxScale,
          ),
        };
      },
      {} as Record<number, boolean>,
    );
  }, [layerSetting, location.resolution]);

  const metaDataByLayer = useMemo(
    () =>
      metadataQuery.data?.layers?.reduce(
        (acc: Record<number, MapServiceLayerMetadata>, layer) => {
          acc[layer.layerId] = layer;
          return acc;
        },
        {},
      ) ?? {},
    [metadataQuery.data],
  );

  if (legendQuery.isLoading || layersConfigQuery.isLoading || mapLayerSetting.isLoading)
    return <EsriLayerSettingsPlaceholder />;

  const subLayersSetting = Object.values(layerSetting)
    .filter((layer) => layer.isGroupLayer || layersConfig[layer.id]?.displayInLegend)
    .sort((a, b) => a.order - b.order);

  const toggleLayerSetting = (id: number, property: 'isEnabled' | 'isExpanded') => {
    if (property === 'isEnabled')
      Tracking.Topics.toggleSubLayerVisibility(
        topicId,
        !layerSetting[id][property] ? 'visible' : 'invisible',
      );
    setSubLayerSetting(topicId, {
      ...layerSetting,
      [id]: {
        ...layerSetting[id],
        [property]: !layerSetting[id][property],
      },
    });
  };

  const onEnable = (id: number) => toggleLayerSetting(id, 'isEnabled');
  const onExpand = (id: number) => toggleLayerSetting(id, 'isExpanded');

  return (
    <GroupLayerSetting
      topicId={topicId}
      settings={subLayersSetting}
      legend={legend}
      metadata={metaDataByLayer}
      visibleSublayerIdsAtScale={visibleSublayerIdsAtScale}
      onExpand={onExpand}
      onEnable={onEnable}
    />
  );
};

export default EsriLayerSettings;
